import { Socket, io } from 'socket.io-client';
import posthog from 'posthog-js';
import dayjs from 'dayjs';
import { Toast } from '@/utils';
import { GetSocketConfig } from './units/socket-config';
import { IS_PROD_ENV } from './request';

export type SocketClientType = ReturnType<typeof GetSocketConfig> & SocketIO;

const ERROR_CODE_2_MESSAGE = {
  40024: '检测到突出部位，为避免接触，已自动调整艾灸高度。离开突出部位后将自动降低高度。',
};

class SocketIO {
  socket: Socket;
  hasMount = null;
  onConnectFunc = null;
  private static REQUEST_TIME_OUT = 10000;
  private robotEventListener = null;

  constructor() {
    if (!this.hasMount) {
      // 将配置挂载至原型上
      // @ts-ignore
      Object.defineProperties(this.__proto__, {
        ...Object.entries(GetSocketConfig(this.request))
          .map(([key, val]) => ({
            [key]: {
              value: val,
            },
          }))
          .reduce(
            (accur, cur) => ({
              ...accur,
              ...cur,
            }),
            {}
          ),
        hasMount: {
          value: true,
        },
      });
    }
  }

  private request: SocketRequest = (params, config) => {
    return new Promise((resolve, reject) => {
      if (!IS_PROD_ENV) {
        // eslint-disable-next-line no-console
        console.log(`%c [Socket] Req: ${params?.service}`, 'color: #52c41a', params);
      }

      this.socket.timeout(config?.timeout || SocketIO.REQUEST_TIME_OUT).emit('request', params, (err, data) => {
        if (!IS_PROD_ENV) {
          const resTime = dayjs().format('MM-DD HH:mm:ss');
          // eslint-disable-next-line no-console
          console.log(`%c [Socket] Res: ${params?.service} [${resTime}]`, 'color: #52c41a', err || data);
          //@ts-ignore
          posthog.sessionRecording?.log({
            type: 'socketIo-log',
            url: params?.service,
            time: resTime,
            data: err || data,
            params,
          });
        }

        if (err) {
          reject(err);
        } else {
          if (data?.code !== 0) {
            data?.message &&
              Toast.fail(ERROR_CODE_2_MESSAGE[data?.code] || data?.message, {
                duration: config?.errorToastDuration || 2000,
              });
            reject(data);
          } else {
            resolve(data?.data);
          }
        }
      });
    });
  };

  init = (url: string, path = '/api/channel'): SocketClientType => {
    this.socket = io(url, {
      ...(path ? { path } : {}),
      forceNew: true,
      timeout: 10000,
      reconnectionDelayMax: 2500,
      // transports: ['websocket'],
      // rejectUnauthorized: false,
    });
    console.log('当前socket连接状态,', this.socket);

    return this as unknown as SocketClientType;
  };

  onRobotEvent(listener: onRoboticEventListener) {
    this.robotEventListener = listener;
    this.socket.on('robot_event', listener);
  }

  removeRobotivent() {
    if (!this.robotEventListener) return;
    this.socket.off('robot_event', this.robotEventListener);
    this.robotEventListener = null;
  }

  onConnect(listener: VoidFunc) {
    this.socket.on('connect', () => {
      const sid = this.socket.id;
      console.log('connect, 成功建立socket连接, SID:', sid);
      posthog.sessionRecording?.log(`connect, 成功建立socket连接, SID: ${sid}`);
      listener?.();

      const engine = this.socket.io.engine;
      console.log('engine.transport.name', engine.transport.name);

      engine.once('upgrade', () => {
        // called when the transport is upgraded (i.e. from HTTP long-polling to WebSocket)
        console.log('upgrade', engine.transport.name); // in most cases, prints "websocket"
      });

      // engine.on('packet', ({ type, data }) => {
      //   // called for each packet received
      //   console.log('packet', type, data);
      // });

      // engine.on('packetCreate', ({ type, data }) => {
      //   // called for each packet sent
      //   console.log('packetCreate', type, data);
      // });

      // engine.on('drain', () => {
      //   // called when the write buffer is drained
      //   console.log('engine drain');
      // });

      // engine.on('close', reason => {
      //   // called when the underlying connection is closed
      //   console.log('engine close', reason);
      // });
    });
    this.socket.on('reconnect', e => {
      console.log('reconnect', e);
    });
    this.socket.on('connect_error', e => {
      console.log('connect_error', e);
    });
    this.socket.on('connect_timeout', e => {
      console.log('connect_timeout', e);
    });
    this.socket.on('ping', e => {
      console.log('ping', e);
    });
    this.socket.on('reconnect_failed', e => {
      console.log('reconnect_failed', e);
    });
    this.socket.on('reconnect_attempt', e => {
      console.log('reconnect_attempt', e);
    });
    this.socket.on('reconnect_error', e => {
      console.log('reconnect_error', e);
    });
  }

  onceConnect(listener: VoidFunc) {
    this.socket.once('connect', () => {
      setTimeout(() => listener?.(), 2000);
    });
  }

  removeEvent() {
    this.socket.off();
  }

  onError(listener: NoopFunc) {
    this.socket.on('error', listener);
  }

  onDisconnect(listener: (reason: Socket.DisconnectReason) => void) {
    this.socket.on('disconnect', reason => {
      console.log('检测到socket连接断开，reason：', reason);
      posthog.sessionRecording?.log(`检测到socket连接断开，reason：${JSON.stringify(reason, null, 2)}`);
      listener?.(reason);
    });
  }

  destroy() {
    this.socket.disconnect();
    this.socket.off();
  }
}

export default new SocketIO();
